import React, { useState } from 'react';
import { User, SendResult, BroadcastResult } from '../types';

// ─── Types ───

interface InlineButton {
  text: string;
  url: string;
}

interface FieldDef {
  name: string;
  label: string;
  type: 'textarea' | 'select' | 'checkbox' | 'number' | 'text';
  required?: boolean;
  options?: string[];
  step?: string;
}

interface TypeConfig {
  label: string;
  description: string;
  hasFile?: boolean;
  accept?: string;
  fields: FieldDef[];
}

interface MessageComposerProps {
  selected: (string | number)[];
  users: User[];
  getUserId: (user: User) => string | number;
  getUserName: (user: User) => string;
  onResult: (data: SendResult | null) => void;
  onBroadcastResult: (data: BroadcastResult) => void;
}

interface ValidationWarning {
  type: 'error' | 'warn';
  msg: string;
}

interface PreviewContent {
  label: string;
  body: string;
  file?: string | null;
  fileUrl?: string | null;
  filePreviewUrl?: string | null;
}

// ─── Helpers ───

function escapeHtml(text: string): string {
  return text.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}

function convertMarkdown(text: string): string {
  let html = escapeHtml(text);
  html = html.replace(/```([\s\S]*?)```/g, '<pre style="background:#e8e8e8;padding:6px 10px;border-radius:4px;overflow-x:auto">$1</pre>');
  html = html.replace(/`([^`]+)`/g, '<code style="background:#e8e8e8;padding:1px 4px;border-radius:3px">$1</code>');
  html = html.replace(/\*([^*]+)\*/g, '<b>$1</b>');
  html = html.replace(/(?<![a-zA-Z0-9])_([^_]+)_(?![a-zA-Z0-9])/g, '<i>$1</i>');
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#2a5db0" target="_blank" rel="noreferrer">$1</a>');
  html = html.replace(/\n/g, '<br/>');
  return html;
}

function convertMarkdownV2(text: string): string {
  let html = escapeHtml(text);
  html = html.replace(/\\([_*\[\]()~`>#+\-=|{}.!\\])/g, '\x00ESC$1\x00');
  html = html.replace(/```([\s\S]*?)```/g, '<pre style="background:#e8e8e8;padding:6px 10px;border-radius:4px;overflow-x:auto">$1</pre>');
  html = html.replace(/`([^`]+)`/g, '<code style="background:#e8e8e8;padding:1px 4px;border-radius:3px">$1</code>');
  html = html.replace(/\*([^*]+)\*/g, '<b>$1</b>');
  html = html.replace(/(?<![a-zA-Z0-9])_([^_]+)_(?![a-zA-Z0-9])/g, '<i>$1</i>');
  html = html.replace(/__([^_]+)__/g, '<u>$1</u>');
  html = html.replace(/~([^~]+)~/g, '<s>$1</s>');
  html = html.replace(/\|\|([^|]+)\|\|/g, '<span style="background:#444;color:#444;border-radius:3px;padding:0 3px;cursor:pointer" title="스포일러 (클릭)">$1</span>');
  html = html.replace(/\[([^\]]+)\]\(([^)]+)\)/g, '<a href="$2" style="color:#2a5db0" target="_blank" rel="noreferrer">$1</a>');
  html = html.replace(/(^|<br\/?>)&gt;([^\n<]*)/g, '$1<div style="border-left:3px solid #a0c4a0;padding-left:8px;color:#555">$2</div>');
  html = html.replace(/\x00ESC(.)\x00/g, '$1');
  html = html.replace(/\n/g, '<br/>');
  return html;
}

function convertTelegramHtml(text: string): string {
  let html = text;
  html = html.replace(/<tg-spoiler>([\s\S]*?)<\/tg-spoiler>/g,
    '<span style="background:#444;color:#444;border-radius:3px;padding:0 3px;cursor:pointer" title="스포일러 (클릭)">$1</span>');
  html = html.replace(/\n/g, '<br/>');
  return html;
}

const TELEGRAM_ALLOWED_TAGS = ['b', 'strong', 'i', 'em', 'u', 'ins', 's', 'strike', 'del', 'code', 'pre', 'a', 'blockquote', 'tg-spoiler', 'span', 'tg-emoji'];

function validateSyntax(text: string, parseMode: string): ValidationWarning[] {
  if (!text) return [];
  const warnings: ValidationWarning[] = [];

  if (parseMode === 'HTML') {
    const openTags = Array.from(text.matchAll(/<([a-z][a-z0-9-]*)\b[^>]*(?<!\/)>/gi));
    const selfClosing = new Set(['br', 'hr', 'img']);
    openTags.forEach(m => {
      const tag = m[1].toLowerCase();
      if (!selfClosing.has(tag) && !TELEGRAM_ALLOWED_TAGS.includes(tag)) {
        warnings.push({ type: 'error', msg: `<${m[1]}> 태그는 텔레그램에서 지원하지 않습니다.` });
      }
    });
    const stack: string[] = [];
    const allTags = Array.from(text.matchAll(/<\/?([a-z][a-z0-9-]*)\b[^>]*>/gi));
    allTags.forEach(m => {
      const raw = m[0]; const tag = m[1].toLowerCase();
      if (selfClosing.has(tag) || raw.endsWith('/>')) return;
      if (raw.startsWith('</')) {
        if (stack.length > 0 && stack[stack.length - 1] === tag) stack.pop();
        else warnings.push({ type: 'error', msg: `</${tag}> 닫는 태그에 대응하는 여는 태그가 없습니다.` });
      } else { stack.push(tag); }
    });
    stack.forEach(tag => warnings.push({ type: 'error', msg: `<${tag}> 태그가 닫히지 않았습니다.` }));
    const aTags = Array.from(text.matchAll(/<a\b([^>]*)>/gi));
    aTags.forEach(m => { if (!/href\s*=/i.test(m[1])) warnings.push({ type: 'warn', msg: `<a> 태그에 href 속성이 없습니다.` }); });
  }

  if (parseMode === 'Markdown') {
    const cbc = (text.match(/```/g) || []).length;
    if (cbc % 2 !== 0) warnings.push({ type: 'error', msg: '``` 코드 블록이 닫히지 않았습니다.' });
    const noCB = text.replace(/```[\s\S]*?```/g, '');
    const btc = (noCB.match(/`/g) || []).length;
    if (btc % 2 !== 0) warnings.push({ type: 'error', msg: '` 인라인 코드가 닫히지 않았습니다.' });
    const noC = noCB.replace(/`[^`]*`/g, '');
    const bc = (noC.match(/(?<!\*)\*(?!\*)/g) || []).length;
    if (bc % 2 !== 0) warnings.push({ type: 'error', msg: '* 굵게(bold) 마크가 닫히지 않았습니다.' });
    const ic = (noC.match(/(?<![a-zA-Z0-9_])_|_(?![a-zA-Z0-9_])/g) || []).length;
    if (ic % 2 !== 0) warnings.push({ type: 'error', msg: '_ 기울임(italic) 마크가 닫히지 않았습니다.' });
    const links = Array.from(noC.matchAll(/\[([^\]]*)\](?!\()/g));
    links.forEach(m => warnings.push({ type: 'warn', msg: `[${m[1]}] 뒤에 (url)이 없습니다.` }));
  }

  if (parseMode === 'MarkdownV2') {
    const cleaned = text.replace(/\\./g, '');
    const cbc = (cleaned.match(/```/g) || []).length;
    if (cbc % 2 !== 0) warnings.push({ type: 'error', msg: '``` 코드 블록이 닫히지 않았습니다.' });
    const noCB = cleaned.replace(/```[\s\S]*?```/g, '');
    const btc = (noCB.match(/`/g) || []).length;
    if (btc % 2 !== 0) warnings.push({ type: 'error', msg: '` 인라인 코드가 닫히지 않았습니다.' });
    const noC = noCB.replace(/`[^`]*`/g, '');
    if ((noC.match(/\*/g) || []).length % 2 !== 0) warnings.push({ type: 'error', msg: '* 굵게(bold) 마크가 닫히지 않았습니다.' });
    const noUL = noC.replace(/__/g, '');
    if ((noUL.match(/_/g) || []).length % 2 !== 0) warnings.push({ type: 'error', msg: '_ 기울임(italic) 마크가 닫히지 않았습니다.' });
    if ((noC.match(/__/g) || []).length % 2 !== 0) warnings.push({ type: 'error', msg: '__ 밑줄(underline) 마크가 닫히지 않았습니다.' });
    if ((noC.match(/~/g) || []).length % 2 !== 0) warnings.push({ type: 'error', msg: '~ 취소선(strikethrough) 마크가 닫히지 않았습니다.' });
    if ((noC.match(/\|\|/g) || []).length % 2 !== 0) warnings.push({ type: 'error', msg: '|| 스포일러 마크가 닫히지 않았습니다.' });
    const specialChars = '_*[]()~`>#+-=|{}.!';
    const unescaped: string[] = [];
    for (let i = 0; i < text.length; i++) {
      if (text[i] === '\\') { i++; continue; }
      if (specialChars.includes(text[i])) {
        const ch = text[i];
        if ('*_~`|>['.includes(ch)) continue;
        if (!unescaped.includes(ch)) unescaped.push(ch);
      }
    }
    if (unescaped.length > 0) warnings.push({ type: 'warn', msg: `${unescaped.map(c => `'${c}'`).join(', ')} 문자를 이스케이프(\\) 처리해야 할 수 있습니다.` });
  }

  return warnings;
}

function formatPreviewText(text: string, parseMode: string): string {
  if (!text) return '';
  switch (parseMode) {
    case 'Markdown': return convertMarkdown(text);
    case 'MarkdownV2': return convertMarkdownV2(text);
    case 'HTML': return convertTelegramHtml(text);
    default: return escapeHtml(text).replace(/\n/g, '<br/>');
  }
}

// ─── Config ───

const TYPE_CONFIG: Record<string, TypeConfig> = {
  text: {
    label: '텍스트', description: '일반 텍스트 메시지 (Markdown/HTML 지원)',
    fields: [
      { name: 'text', label: '메시지', type: 'textarea', required: true },
      { name: 'parse_mode', label: '포맷', type: 'select', options: ['', 'Markdown', 'MarkdownV2', 'HTML'] },
    ],
  },
  photo: {
    label: '사진', description: '이미지 파일 또는 URL (최대 10MB)', hasFile: true, accept: 'image/*',
    fields: [
      { name: 'caption', label: '캡션', type: 'textarea' },
      { name: 'parse_mode', label: '캡션 포맷', type: 'select', options: ['', 'Markdown', 'MarkdownV2', 'HTML'] },
    ],
  },
  document: {
    label: '파일', description: '문서/파일 전송 (최대 50MB)', hasFile: true, accept: '*/*',
    fields: [
      { name: 'media_name', label: '파일명', type: 'text' },
      { name: 'caption', label: '캡션', type: 'textarea' },
      { name: 'parse_mode', label: '캡션 포맷', type: 'select', options: ['', 'Markdown', 'MarkdownV2', 'HTML'] },
    ],
  },
  video: {
    label: '동영상', description: 'MP4 동영상 파일 또는 URL (최대 50MB)', hasFile: true, accept: 'video/*',
    fields: [
      { name: 'caption', label: '캡션', type: 'textarea' },
      { name: 'parse_mode', label: '캡션 포맷', type: 'select', options: ['', 'Markdown', 'MarkdownV2', 'HTML'] },
    ],
  },
  animation: {
    label: 'GIF', description: 'GIF 애니메이션 파일 또는 URL (최대 50MB)', hasFile: true, accept: 'image/gif,video/mp4',
    fields: [
      { name: 'caption', label: '캡션', type: 'textarea' },
      { name: 'parse_mode', label: '캡션 포맷', type: 'select', options: ['', 'Markdown', 'MarkdownV2', 'HTML'] },
    ],
  },
};

const styles = {
  typeGrid: { display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(100px, 1fr))', gap: '8px', marginBottom: '20px' } as React.CSSProperties,
  typeBtn: (active: boolean): React.CSSProperties => ({
    padding: '10px 8px', border: active ? '2px solid #1976d2' : '1px solid #ddd', borderRadius: '8px',
    background: active ? '#e3f2fd' : '#fff', cursor: 'pointer', textAlign: 'center', fontSize: '13px',
    fontWeight: active ? 600 : 400, transition: 'all 0.15s',
  }),
  fieldGroup: { marginBottom: '14px' } as React.CSSProperties,
  label: { display: 'block', marginBottom: '4px', fontSize: '13px', fontWeight: 600, color: '#444' } as React.CSSProperties,
  input: { width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', boxSizing: 'border-box' as const } as React.CSSProperties,
  textarea: { width: '100%', padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', resize: 'vertical' as const, boxSizing: 'border-box' as const } as React.CSSProperties,
  select: { padding: '8px 10px', border: '1px solid #ccc', borderRadius: '6px', fontSize: '14px', background: '#fff' } as React.CSSProperties,
  desc: { fontSize: '12px', color: '#888', margin: '0 0 16px' } as React.CSSProperties,
};

// ─── Component ───

export default function MessageComposer({ selected, users, getUserId, getUserName, onResult, onBroadcastResult }: MessageComposerProps) {
  const [type, setType] = useState('text');
  const [fields, setFields] = useState<Record<string, string | boolean>>({});
  const [file, setFile] = useState<File | null>(null);
  const [fileUrl, setFileUrl] = useState('');
  const [buttons, setButtons] = useState<InlineButton[]>([]);
  const [sending, setSending] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [broadcastMode, setBroadcastMode] = useState(false);

  const config = TYPE_CONFIG[type];

  const updateField = (name: string, value: string | boolean) => setFields(prev => ({ ...prev, [name]: value }));

  const handleTypeChange = (newType: string) => {
    setType(newType); setFields({}); setFile(null); setFileUrl(''); setButtons([]);
  };

  const canSend = (): boolean => {
    if (!broadcastMode && selected.length === 0) return false;
    if (sending) return false;
    if (type === 'text') return !!(fields.text as string)?.trim();
    return !!(file || fileUrl.trim());
  };

  const handleSend = async () => {
    if (!canSend()) return;
    setShowPreview(false); setSending(true);
    if (!broadcastMode) onResult(null);

    try {
      const endpoint = broadcastMode ? '/api/telegram/broadcast' : '/api/telegram/send';
      const validButtons = buttons.filter(b => b.text?.trim() && b.url?.trim());
      let res: Response;

      if (file) {
        const formData = new FormData();
        if (!broadcastMode) formData.append('userIds', JSON.stringify(selected));
        formData.append('type', type);
        formData.append('file', file);
        for (const [key, value] of Object.entries(fields)) {
          if (value !== undefined && value !== '') formData.append(key, String(value));
        }
        if (type === 'text' && fields.text) formData.append('message', fields.text as string);
        if (validButtons.length > 0) formData.append('buttons', JSON.stringify(validButtons));
        res = await fetch(endpoint, { method: 'POST', body: formData });
      } else {
        const body: Record<string, unknown> = { type, ...fields };
        if (!broadcastMode) body.userIds = selected;
        if (type === 'text' && fields.text) body.message = fields.text;
        if (fileUrl.trim()) body.fileUrl = fileUrl.trim();
        if (validButtons.length > 0) body.buttons = validButtons;
        res = await fetch(endpoint, { method: 'POST', headers: { 'Content-Type': 'application/json' }, body: JSON.stringify(body) });
      }

      const data = await res.json();
      if (broadcastMode) onBroadcastResult(data.error ? { error: data.error } : data as BroadcastResult);
      else onResult(data.error ? { error: data.error } as SendResult : data as SendResult);
    } catch {
      const errResult = { error: '전송 중 오류가 발생했습니다.' };
      if (broadcastMode) onBroadcastResult(errResult); else onResult(errResult as SendResult);
    }
    setSending(false);
  };

  const renderField = (fieldDef: FieldDef) => {
    const { name, label, type: fType, required, options, step } = fieldDef;
    const value = fields[name] || '';
    switch (fType) {
      case 'textarea':
        return (<div key={name} style={styles.fieldGroup}><label style={styles.label}>{label}{required && ' *'}</label><textarea value={value as string} onChange={e => updateField(name, e.target.value)} rows={3} style={styles.textarea} /></div>);
      case 'select':
        return (<div key={name} style={styles.fieldGroup}><label style={styles.label}>{label}</label><select value={value as string} onChange={e => updateField(name, e.target.value)} style={styles.select}>{options!.map(o => (<option key={o} value={o}>{o || '선택 안 함'}</option>))}</select></div>);
      case 'checkbox':
        return (<div key={name} style={{ ...styles.fieldGroup, display: 'flex', alignItems: 'center', gap: '8px' }}><input type="checkbox" checked={value === true || value === 'true'} onChange={e => updateField(name, e.target.checked)} /><label style={{ fontSize: '13px', color: '#444' }}>{label}</label></div>);
      case 'number':
        return (<div key={name} style={styles.fieldGroup}><label style={styles.label}>{label}{required && ' *'}</label><input type="number" value={value as string} step={step || '1'} onChange={e => updateField(name, e.target.value)} style={styles.input} /></div>);
      default:
        return (<div key={name} style={styles.fieldGroup}><label style={styles.label}>{label}{required && ' *'}</label><input type="text" value={value as string} onChange={e => updateField(name, e.target.value)} style={styles.input} /></div>);
    }
  };

  const renderFileInput = () => (
    <div style={styles.fieldGroup}>
      <label style={styles.label}>파일 업로드</label>
      <input type="file" accept={config.accept} onChange={e => { setFile(e.target.files?.[0] || null); if (e.target.files?.[0]) setFileUrl(''); }} style={{ fontSize: '13px' }} />
      <div style={{ margin: '8px 0', fontSize: '13px', color: '#888' }}>또는</div>
      <label style={styles.label}>URL로 전송</label>
      <input type="text" value={fileUrl} placeholder="https://example.com/file.jpg" onChange={e => { setFileUrl(e.target.value); if (e.target.value) setFile(null); }} style={styles.input} />
    </div>
  );

  const renderButtons = () => (
    <div style={{ ...styles.fieldGroup, marginTop: '8px', padding: '12px', background: '#f9f9f9', borderRadius: '6px' }}>
      <label style={{ ...styles.label, marginBottom: '8px' }}>인라인 버튼 (선택사항)</label>
      {buttons.map((btn, i) => (
        <div key={i} style={{ display: 'flex', gap: '8px', marginBottom: '6px', alignItems: 'center' }}>
          <input type="text" value={btn.text} placeholder="버튼 텍스트" onChange={e => { const next = [...buttons]; next[i] = { ...next[i], text: e.target.value }; setButtons(next); }} style={{ ...styles.input, flex: 1 }} />
          <input type="text" value={btn.url} placeholder="https://..." onChange={e => { const next = [...buttons]; next[i] = { ...next[i], url: e.target.value }; setButtons(next); }} style={{ ...styles.input, flex: 1 }} />
          <button onClick={() => setButtons(buttons.filter((_, j) => j !== i))} style={{ background: 'none', border: 'none', color: '#d32f2f', cursor: 'pointer', fontSize: '18px', padding: '4px' }}>x</button>
        </div>
      ))}
      {buttons.length < 10 && (
        <button onClick={() => setButtons([...buttons, { text: '', url: '' }])} style={{ background: 'none', border: '1px dashed #ccc', borderRadius: '6px', padding: '6px 12px', cursor: 'pointer', fontSize: '13px', color: '#666', marginTop: '4px' }}>+ 버튼 추가</button>
      )}
    </div>
  );

  const getPreviewContent = (): PreviewContent => {
    if (type === 'text') return { label: '텍스트', body: (fields.text as string) || '' };
    return {
      label: config.label,
      file: file ? file.name : null,
      fileUrl: fileUrl || null,
      filePreviewUrl: file && type === 'photo' ? URL.createObjectURL(file) : null,
      body: (fields.caption as string) || '',
    };
  };

  const renderPreviewModal = () => {
    const preview = getPreviewContent();
    const recipients = broadcastMode
      ? ['전체 사용자 (Broadcast)']
      : selected.map(uid => { const u = users.find(usr => String(getUserId(usr)) === String(uid)); return u ? getUserName(u) : String(uid); });
    const textToValidate = (type === 'text' ? fields.text : fields.caption) as string;
    const validationWarnings = fields.parse_mode ? validateSyntax(textToValidate || '', fields.parse_mode as string) : [];
    const hasErrors = validationWarnings.some(w => w.type === 'error');
    const validButtons = buttons.filter(b => b.text?.trim() && b.url?.trim());

    return (
      <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, background: 'rgba(0,0,0,0.5)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000 }} onClick={() => setShowPreview(false)}>
        <div style={{ background: '#fff', borderRadius: '12px', width: '480px', maxWidth: '90vw', maxHeight: '80vh', overflow: 'auto', boxShadow: '0 8px 32px rgba(0,0,0,0.2)' }} onClick={e => e.stopPropagation()}>
          <div style={{ padding: '16px 20px', borderBottom: '1px solid #eee', fontWeight: 700, fontSize: '16px' }}>{broadcastMode ? '일괄 전송 미리보기' : '전송 미리보기'}</div>
          <div style={{ padding: '12px 20px', background: broadcastMode ? '#fff3e0' : '#f9f9f9', borderBottom: '1px solid #eee' }}>
            <div style={{ fontSize: '12px', color: '#888', marginBottom: '4px' }}>{broadcastMode ? '수신 대상' : `수신자 (${recipients.length}명)`}</div>
            <div style={{ fontSize: '13px', color: broadcastMode ? '#e65100' : '#333', fontWeight: broadcastMode ? 600 : 400 }}>{recipients.join(', ')}</div>
          </div>
          <div style={{ padding: '20px', background: '#e8effa' }}>
            <div style={{ background: '#dcf8c6', borderRadius: '8px 8px 0 8px', padding: '10px 14px', maxWidth: '85%', marginLeft: 'auto', boxShadow: '0 1px 2px rgba(0,0,0,0.1)', wordBreak: 'break-word' }}>
              {type !== 'text' && <div style={{ display: 'inline-block', background: '#a8d5a2', color: '#2e5c2a', fontSize: '11px', fontWeight: 600, padding: '2px 8px', borderRadius: '4px', marginBottom: '8px' }}>{preview.label}</div>}
              {preview.filePreviewUrl && <div style={{ marginBottom: '8px' }}><img src={preview.filePreviewUrl} alt="preview" style={{ maxWidth: '100%', borderRadius: '6px', maxHeight: '200px', objectFit: 'cover' }} /></div>}
              {preview.file && !preview.filePreviewUrl && <div style={{ padding: '8px 12px', background: '#c8e6c9', borderRadius: '6px', marginBottom: '8px', fontSize: '13px', color: '#2e7d32' }}>{preview.file}</div>}
              {preview.fileUrl && !preview.file && <div style={{ padding: '8px 12px', background: '#c8e6c9', borderRadius: '6px', marginBottom: '8px', fontSize: '13px', color: '#2e7d32', wordBreak: 'break-all' }}>{preview.fileUrl}</div>}
              {preview.body && <div style={{ fontSize: '14px', lineHeight: '1.5' }} dangerouslySetInnerHTML={{ __html: formatPreviewText(preview.body, fields.parse_mode as string) }} />}
              {fields.parse_mode && <div style={{ fontSize: '11px', color: '#6b8e63', marginTop: '4px' }}>({fields.parse_mode as string})</div>}
            </div>
            {validButtons.length > 0 && <div style={{ maxWidth: '85%', marginLeft: 'auto', marginTop: '4px' }}>{validButtons.map((btn, i) => <div key={i} style={{ background: '#fff', border: '1px solid #b0c4de', borderRadius: '6px', padding: '8px 12px', marginTop: '4px', textAlign: 'center', fontSize: '13px', color: '#1976d2', fontWeight: 500, cursor: 'pointer' }}>{btn.text}</div>)}</div>}
          </div>
          {validationWarnings.length > 0 && (
            <div style={{ padding: '12px 20px', borderTop: '1px solid #eee' }}>
              <div style={{ fontSize: '13px', fontWeight: 600, marginBottom: '6px', color: hasErrors ? '#d32f2f' : '#ed6c02' }}>{hasErrors ? '문법 오류 발견' : '문법 경고'}</div>
              {validationWarnings.map((w, i) => (
                <div key={i} style={{ display: 'flex', alignItems: 'flex-start', gap: '6px', padding: '4px 0', fontSize: '13px' }}>
                  <span style={{ display: 'inline-block', minWidth: '18px', height: '18px', lineHeight: '18px', borderRadius: '50%', textAlign: 'center', fontSize: '11px', fontWeight: 700, color: '#fff', background: w.type === 'error' ? '#d32f2f' : '#ed6c02' }}>{w.type === 'error' ? '!' : '?'}</span>
                  <span style={{ color: w.type === 'error' ? '#c62828' : '#e65100' }}>{w.msg}</span>
                </div>
              ))}
            </div>
          )}
          {fields.parse_mode && validationWarnings.length === 0 && <div style={{ padding: '10px 20px', borderTop: '1px solid #eee' }}><span style={{ fontSize: '12px', color: '#2e7d32', fontWeight: 600, background: '#e8f5e9', padding: '3px 10px', borderRadius: '12px' }}>{fields.parse_mode as string} 문법 검증 통과</span></div>}
          <div style={{ padding: '16px 20px', borderTop: '1px solid #eee', display: 'flex', alignItems: 'center', justifyContent: 'flex-end', gap: '8px' }}>
            {hasErrors && <span style={{ fontSize: '12px', color: '#d32f2f', marginRight: 'auto' }}>문법 오류가 있습니다. 전송 시 실패할 수 있습니다.</span>}
            <button onClick={() => setShowPreview(false)} style={{ padding: '8px 20px', border: '1px solid #ccc', borderRadius: '6px', background: '#fff', cursor: 'pointer', fontSize: '14px' }}>취소</button>
            <button onClick={handleSend} style={{ padding: '8px 20px', border: 'none', borderRadius: '6px', background: hasErrors ? '#ef5350' : broadcastMode ? '#e65100' : '#1976d2', color: '#fff', cursor: 'pointer', fontSize: '14px' }}>{hasErrors ? '그래도 전송' : broadcastMode ? '전체 전송' : '전송'}</button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div>
      {showPreview && renderPreviewModal()}
      <div style={styles.typeGrid}>
        {Object.entries(TYPE_CONFIG).map(([key, cfg]) => <div key={key} onClick={() => handleTypeChange(key)} style={styles.typeBtn(type === key)}>{cfg.label}</div>)}
      </div>
      <p style={styles.desc}>{config.description}</p>
      {config.hasFile && renderFileInput()}
      {config.fields.map(renderField)}
      {renderButtons()}
      <div style={{ marginTop: '12px', display: 'flex', gap: '8px', alignItems: 'center', flexWrap: 'wrap' }}>
        <button onClick={() => { setBroadcastMode(false); setShowPreview(true); }} disabled={!canSend()}
          style={{ padding: '10px 24px', background: canSend() ? '#1976d2' : '#ccc', color: '#fff', border: 'none', borderRadius: '6px', cursor: canSend() ? 'pointer' : 'not-allowed', fontSize: '14px' }}>
          {sending && !broadcastMode ? '전송 중...' : '미리보기 & 전송'}
        </button>
        <button onClick={() => { setBroadcastMode(true); setShowPreview(true); }}
          disabled={sending || (type === 'text' ? !(fields.text as string)?.trim() : !(file || fileUrl.trim()))}
          style={{ padding: '10px 24px', background: (!sending && (type === 'text' ? !!(fields.text as string)?.trim() : !!(file || fileUrl.trim()))) ? '#e65100' : '#ccc', color: '#fff', border: 'none', borderRadius: '6px', cursor: (!sending && (type === 'text' ? !!(fields.text as string)?.trim() : !!(file || fileUrl.trim()))) ? 'pointer' : 'not-allowed', fontSize: '14px' }}>
          {sending && broadcastMode ? '전송 중...' : '전체 전송 (Broadcast)'}
        </button>
      </div>
    </div>
  );
}
